import { IAPITarget, ITypedAPITarget } from "network/NetworkStapler";
import { numberToEuroAmount } from "types/amount";
import { ICardNameValidResponse, ILoyaltyDataResponse, ILoyaltyDataType, ILoyaltyForexRefundRequest, ILoyaltyForexRefundResponse, ILoyaltyForexTransactionProductType, ILoyaltyForexTransactionsResponse, ILoyaltyHistoryResponse, IPayCardFeeInMilesDetailResponse, IPayCardFeeInMilesPutResponse, IPin, ISecuredInfoDisplayChannelResponse, ISecuredInfoResponseGet, ISecuredInfoResponsePost } from "types/card";
import { ContactAddressResponse } from "types/contact";
import { IGetGooglePayApprovalResponse, IPostGooglePayApprovalResponse } from "types/googlePay";
import { IOffer } from "types/offer";
import { ISignInfoResponse, ISigningInfo } from "types/signing";
import { ITeamcardLinkageCardData } from "types/teamcardLinkage";

/**
 * This is the actual call requesting a new card PIN
 * @param cardId 
 */
export function displayPin(cardId: string): ITypedAPITarget<ISigningInfo> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/states",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.cards.model.card.state.v1+json",
            "Content-Type": "application/vnd.at.spardat.rest.cards.model.card.state.v1+json"
        },
        method: "PUT",
        body: {
            action: "DISPLAY_PIN"
        },
        parse: (json: any) => {
            return json.signInfo as ISigningInfo;
        }
    };
}

/**
 * This is the actual call requesting a new card PIN
 * @param cardId 
 */
export function postPin(cardId: string): ITypedAPITarget<ISigningInfo> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/pin",
        headers: {
            "Content-Type": "application/json"
        },
        method: "POST",
        body: {
            action: "DISPLAY_PIN"
        },
        parse: (json: any) => {
            return json.signInfo as ISigningInfo;
        }
    };
}

/**
 * get the new pin
 */
export function getPin(cardId: string, pinId: string): ITypedAPITarget<IPin> {
    return {
        url: "/rest/netbanking/my/cards/" + cardId + "/pin/" + pinId,
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as IPin;
        }
    };
}

export function putTransfer(cardId: string, amount: number, directDebitIban: string): IAPITarget {
    return {
        url: "rest/netbanking/my/cards/" + cardId + "/transfer",
        headers: {
            "Accept": "application/vnd.at.spardat.rest.cards.model.card.transfer.v1+json",
            "Content-Type": "application/vnd.at.spardat.rest.cards.model.card.transfer.v1+json"
        },
        method: "PUT",
        body: {
            type: "QUICK_DRAWDOWN",
            receiver: {
                accountno: {
                    iban: directDebitIban
                }
            },
            amount: numberToEuroAmount(amount),
        }
    };
}

export function getLoyalty(cardId: string): ITypedAPITarget<ILoyaltyDataResponse> {
    return {
        url: "cards/" + cardId + "/loyalty",
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as ILoyaltyDataResponse;
        }
    };
}

export function getPayCardFeeInMilesDetail(
    cardId: string
): ITypedAPITarget<IPayCardFeeInMilesDetailResponse> {
    return {
        url:
            "cards/" +
            cardId +
            "/loyalty/MILES_AND_MORE_PAY_CARD_FEE_IN_MILES",
        headers: {
            Accept: "application/json",
        },
        method: "GET",
        parse: (json: any) => json as IPayCardFeeInMilesDetailResponse,
    };
}

export function putPayCardFeeInMiles(
    cardId: string,
    body: { payCardsFeeInMilesActivated: boolean }
): ITypedAPITarget<IPayCardFeeInMilesPutResponse> {
    return {
        url:
            "cards/" +
            cardId +
            "/loyalty/MILES_AND_MORE_PAY_CARD_FEE_IN_MILES",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        method: "PUT",
        body,
        parse: (json: any) => json as IPayCardFeeInMilesPutResponse,
    };
}

export function getLoyaltyHistory(cardId: string, type: ILoyaltyDataType, year: number): ITypedAPITarget<ILoyaltyHistoryResponse> {
    return {
        url: "cards/" + cardId + "/loyalty/history/" + type + "?year=" + year,
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as ILoyaltyHistoryResponse;
        }
    };
}

export function getLoyaltyHistoryPeriod(url: string): ITypedAPITarget<ILoyaltyHistoryResponse> {
    return {
        url: url,
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as ILoyaltyHistoryResponse;
        }
    };
}

export function getLoyaltyHistoryPeriods(url: string): ITypedAPITarget<{ periods: ILoyaltyHistoryResponse[] }> {
    return {
        url: url,
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as { periods: ILoyaltyHistoryResponse[] };
        }
    };
}

export function getForexTransactions(filterFor: ILoyaltyForexTransactionProductType, filterProductId: string): ITypedAPITarget<ILoyaltyForexTransactionsResponse> {
    return {
        url: "products/forex/transactions?filterFor=" + filterFor + "&filterProductId=" + filterProductId,
        headers: {
            "Accept": "application/json"
        },
        method: "GET",
        parse: (json: any) => {
            return json as ILoyaltyForexTransactionsResponse;
        }
    };
}

export function postRefundForex(body: ILoyaltyForexRefundRequest): ITypedAPITarget<ILoyaltyForexRefundResponse> {
    return {
        url: "products/forex/refund",
        headers: {
            "Accept": "application/json"
        },
        method: "POST",
        body,
        parse: (json: any) => {
            return json as ILoyaltyForexRefundResponse;
        }
    };
}

export function getGooglePayApproval(): ITypedAPITarget<IGetGooglePayApprovalResponse> {
    return {
        url: "/rest/netbanking/my/cards/googlepay/approval",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        parse: json => json as IGetGooglePayApprovalResponse
    };
}

export function postGooglePayApproval(): ITypedAPITarget<IPostGooglePayApprovalResponse> {
    return {
        url: "/rest/netbanking/my/cards/googlepay/approval",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "POST",
        parse: json => json as IPostGooglePayApprovalResponse
    };
}

export function getCardDataDisplayChannel(): ITypedAPITarget<ISecuredInfoDisplayChannelResponse> {
    return {
        url: "/rest/netbanking/my/products/secured-info/at/display-channel",
        headers: {
            "Accept": "application/json",
        },
        method: "GET",
        parse: (json: any) => json as ISecuredInfoDisplayChannelResponse
    };
}

export function getOfferDetails(offer: IOffer): ITypedAPITarget<any> {
    return getOfferDetailsWithIds(offer.productId, offer.conditionsGroupId);
}

export function getOfferDetailsWithIds(productId: string, conditionsGroupId: string): ITypedAPITarget<any> {
    return {
        url: `/rest/netbanking/my/offers/${productId}/${conditionsGroupId}`,
        headers: {
            "Accept": "application/json", // "application/vnd.at.spardat.rest.netbanking.model.offer+json",
        },
        parse: (json: any) => {
            return json;
        }
    };
}

export function postCardData(cardId: string, phoneId?: string): ITypedAPITarget<ISecuredInfoResponsePost> {
    return {
        url: "/rest/netbanking/my/products/secured-info",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        method: "POST",
        body: {
            securedInfoTypes: [
                "CARD_PAN",
                "CARD_CVC"
            ],
            product: {
                type: "CARDS",
                id: cardId
            },
            ...(phoneId ? {
                "at-phone": {
                    id: phoneId
                }
            } : {})
        },
        parse: (json: any) => json as ISecuredInfoResponsePost
    };
}

export function getCardData(domainServiceResponseId: string): ITypedAPITarget<ISecuredInfoResponseGet> {
    return {
        url: `/rest/netbanking/my/products/secured-info/${domainServiceResponseId}`,
        headers: {
            "Accept": "application/json",
        },
        method: "GET",
        parse: (json: any) => json as ISecuredInfoResponseGet
    };
}

export function getInvitationData(invitationId: string): ITypedAPITarget<ITeamcardLinkageCardData> {
    return {
        url: "/rest/netbanking/my/cards/invitations/" + invitationId,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "GET",
        parse: json => json as ITeamcardLinkageCardData
    };
}

export function acceptInvitation(invitationId: string, emailId: string): ITypedAPITarget<ISignInfoResponse> {
    return {
        url: `/rest/netbanking/my/cards/invitations/${invitationId}/accept`,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "POST",
        body: {
            emailId
        },
        parse: json => json as ISignInfoResponse
    };
}

export function getContactsForBusinessCardsInRetail(accountId?: string): ITypedAPITarget<ContactAddressResponse[]> {
    return {
        url: `rest/netbanking/my/accounts/${accountId}/contacts/addresses`,
        headers: {
            "Accept": "application/json",
        },
        parse: (json: any) => {
            return json;
        }
    };
}

export const NAME_ON_CARD_URL = "/rest/netbanking/my/credit-cards/name-on-card";

export function getIsCardNameValid(accountId: string, productId: string, conditionsGroupId: string): ITypedAPITarget<ICardNameValidResponse> {
    return {
        url: NAME_ON_CARD_URL,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        method: "GET",
        queryParameters: {
            accountId,
            productId,
            conditionsGroupId,
        },
        parse: json => json as ICardNameValidResponse
    };
}




// WEBPACK FOOTER //
// ./src/network/CardAPI.ts